import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { X, ArrowLeft, Plus, Check, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { Navbar, Footer } from '@/components/Layout';
import { compareApi, carsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const getSessionId = () => {
  let sessionId = localStorage.getItem('comparison_session_id');
  if (!sessionId) {
    sessionId = 'cmp_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('comparison_session_id', sessionId);
  }
  return sessionId;
};

export default function ComparePage() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allCars, setAllCars] = useState([]);
  const [showCarPicker, setShowCarPicker] = useState(false);
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();

  useEffect(() => {
    loadComparison();
    loadAllCars();
  }, []);

  const loadComparison = async () => {
    try {
      const sessionId = getSessionId();
      const response = await compareApi.get(sessionId);
      setCars(response.data.cars || []);
    } catch (error) {
      console.error('Error loading comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllCars = async () => {
    try {
      const response = await carsApi.getAll({ is_active: true });
      setAllCars(response.data);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const addCar = async (carId) => {
    if (cars.length >= 4) {
      toast.error(t('compare_max_error'));
      return;
    }
    if (cars.some(c => c.id === carId)) {
      toast.error(t('compare_already_error'));
      return;
    }
    
    try {
      const sessionId = getSessionId();
      const newCarIds = [...cars.map(c => c.id), carId];
      await compareApi.set(newCarIds, sessionId);
      await loadComparison();
      setShowCarPicker(false);
      toast.success(t('compare_added'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const removeCar = async (carId) => {
    try {
      const sessionId = getSessionId();
      await compareApi.remove(sessionId, carId);
      await loadComparison();
      toast.success(t('compare_removed'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const clearComparison = async () => {
    try {
      const sessionId = getSessionId();
      await compareApi.clear(sessionId);
      setCars([]);
      toast.success(t('compare_cleared'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(language === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Get available cars (not already in comparison)
  const availableCars = allCars.filter(c => !cars.some(cc => cc.id === c.id));

  // Comparison specs
  const specs = [
    { key: 'price', label: t('detail_price'), format: formatPrice },
    { key: 'year', label: t('detail_year') },
    { key: 'horsepower', label: t('detail_horsepower'), suffix: ` ${t('hp')}` },
    { key: 'fuel_type', label: t('detail_fuel') },
    { key: 'transmission', label: t('detail_transmission') },
    { key: 'doors', label: t('detail_doors') },
    { key: 'color', label: t('detail_color') },
  ];

  // Find best/worst for each spec (for highlighting)
  const getSpecComparison = (key) => {
    if (cars.length < 2) return {};
    const values = cars.map(c => c[key]);
    
    if (key === 'price') {
      const min = Math.min(...values);
      const max = Math.max(...values);
      return { best: min, worst: max };
    }
    if (key === 'horsepower' || key === 'year') {
      const max = Math.max(...values);
      const min = Math.min(...values);
      return { best: max, worst: min };
    }
    return {};
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 flex justify-center">
          <div className="spinner" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{language === 'de' ? 'Fahrzeugvergleich | ' : 'Vehicle Comparison | '}{settings?.site_name || 'Tim Automobile'}</title>
        <meta name="description" content={language === 'de' 
          ? 'Vergleichen Sie Premium-Fahrzeuge nebeneinander. Alle technischen Daten, Preise und Ausstattungen auf einen Blick.'
          : 'Compare premium vehicles side by side. All specifications, prices and features at a glance.'
        } />
        <meta name="keywords" content={language === 'de' 
          ? 'Fahrzeugvergleich, Auto Vergleich, Premium Autos vergleichen, Sportwagen Vergleich'
          : 'Vehicle comparison, Car comparison, Compare premium cars, Sports car comparison'
        } />
        <meta property="og:title" content={language === 'de' ? 'Fahrzeugvergleich' : 'Vehicle Comparison'} />
        <meta property="og:description" content={language === 'de' 
          ? 'Premium-Fahrzeuge nebeneinander vergleichen'
          : 'Compare premium vehicles side by side'
        } />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <Link 
                to="/cars" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2"
              >
                <ArrowLeft size={16} />
                <span className="text-sm">{t('back_to_inventory')}</span>
              </Link>
              <h1 className="font-heading text-3xl md:text-4xl font-light">{t('compare_title')}</h1>
              <p className="text-muted-foreground mt-1">{t('compare_subtitle')}</p>
            </div>
            {cars.length > 0 && (
              <Button
                onClick={clearComparison}
                className="btn-secondary text-red-500 border-red-500 hover:bg-red-50"
                data-testid="clear-comparison-btn"
              >
                {t('compare_clear')}
              </Button>
            )}
          </div>

          {cars.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20 bg-card border border-border rounded-lg"
            >
              <p className="text-muted-foreground mb-4">{t('compare_empty')}</p>
              <Button
                onClick={() => setShowCarPicker(true)}
                className="btn-primary"
                data-testid="add-first-car-btn"
              >
                {t('compare_add')}
              </Button>
            </motion.div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full bg-card border border-border">
                {/* Car Images & Names */}
                <thead>
                  <tr className="border-b border-border">
                    <th className="p-4 text-left font-normal overline w-40 bg-muted">{t('compare_vehicle')}</th>
                    {cars.map((car, index) => (
                      <th key={car.id} className="p-4 text-center min-w-[200px] relative">
                        <button
                          onClick={() => removeCar(car.id)}
                          className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-red-500"
                          data-testid={`remove-car-${index}`}
                        >
                          <X size={16} />
                        </button>
                        <Link to={`/cars/${car.id}`}>
                          <img
                            src={car.images?.[0] || 'https://via.placeholder.com/300x200?text=No+Image'}
                            alt={`${car.brand} ${car.model}`}
                            className="w-full h-36 object-cover mb-3"
                          />
                          <p className="overline text-muted-foreground">{car.brand}</p>
                          <p className="font-heading text-lg">{car.model}</p>
                        </Link>
                      </th>
                    ))}
                    {cars.length < 4 && (
                      <th className="p-4 min-w-[200px]">
                        <button
                          onClick={() => setShowCarPicker(true)}
                          className="w-full h-36 border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                          data-testid="add-car-btn"
                        >
                          <Plus size={32} />
                          <span className="text-sm">{t('compare_add_short')}</span>
                        </button>
                      </th>
                    )}
                  </tr>
                </thead>
                
                {/* Specs Comparison */}
                <tbody>
                  {specs.map((spec) => {
                    const comparison = getSpecComparison(spec.key);
                    return (
                      <tr key={spec.key} className="border-b border-border">
                        <td className="p-4 bg-muted overline">{spec.label}</td>
                        {cars.map((car) => {
                          const value = car[spec.key];
                          const isBest = comparison.best !== undefined && value === comparison.best;
                          const isWorst = comparison.worst !== undefined && value === comparison.worst && comparison.best !== comparison.worst;
                          
                          let displayValue = spec.format ? spec.format(value) : value;
                          if (spec.suffix) displayValue = `${value}${spec.suffix}`;
                          
                          return (
                            <td 
                              key={car.id} 
                              className={`p-4 text-center ${isBest ? 'bg-green-500/10 text-green-500 font-medium' : ''} ${isWorst && spec.key === 'price' ? 'bg-red-500/10 text-red-500' : ''}`}
                            >
                              <span className="flex items-center justify-center gap-2">
                                {displayValue}
                                {isBest && spec.key !== 'color' && spec.key !== 'fuel_type' && spec.key !== 'transmission' && (
                                  <Check size={16} className="text-green-500" />
                                )}
                              </span>
                            </td>
                          );
                        })}
                        {cars.length < 4 && <td className="p-4 bg-muted" />}
                      </tr>
                    );
                  })}
                  
                  {/* Action Row */}
                  <tr>
                    <td className="p-4 bg-muted"></td>
                    {cars.map((car) => (
                      <td key={car.id} className="p-4 text-center">
                        <Link
                          to={`/cars/${car.id}`}
                          className="btn-primary inline-block"
                        >
                          {t('compare_view_details')}
                        </Link>
                      </td>
                    ))}
                    {cars.length < 4 && <td className="p-4 bg-muted" />}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Comparison Tips */}
          {cars.length > 0 && (
            <div className="mt-8 p-6 bg-card border border-border rounded-lg">
              <h3 className="font-heading text-lg mb-3">{t('compare_tips_title')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <Check size={16} className="text-green-500 mt-0.5" />
                  <span>{t('compare_tip_best')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Minus size={16} className="text-red-500 mt-0.5" />
                  <span>{t('compare_tip_price')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Plus size={16} className="text-primary mt-0.5" />
                  <span>{t('compare_tip_add')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Car Picker Modal */}
      {showCarPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden"
          >
            <div className="p-6 border-b border-border flex justify-between items-center">
              <h2 className="font-heading text-xl">{t('compare_picker_title')}</h2>
              <button
                onClick={() => setShowCarPicker(false)}
                className="p-2 hover:bg-muted rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {availableCars.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('compare_all_added')}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableCars.map((car) => (
                    <button
                      key={car.id}
                      onClick={() => addCar(car.id)}
                      className="text-left border border-border hover:border-primary p-3 transition-colors"
                      data-testid={`picker-car-${car.id}`}
                    >
                      <img
                        src={car.images?.[0] || 'https://via.placeholder.com/300x200?text=No+Image'}
                        alt={`${car.brand} ${car.model}`}
                        className="w-full h-32 object-cover mb-2"
                      />
                      <p className="text-xs text-muted-foreground">{car.brand}</p>
                      <p className="font-medium">{car.model}</p>
                      <p className="text-primary text-sm">{formatPrice(car.price)}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
}
